import { AlertCircle, Loader2, Mail, MapPin } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import ContactForm from "@/components/site/ContactForm";
import SEO from "@/components/site/SEO";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { getPage } from "@/lib/api";
import { useAsync } from "@/lib/useAsync";
import { useSite } from "@/lib/SiteContext";
import { buildBreadcrumbs } from "@/lib/breadcrumbs";

/**
 * /contact — renders the CMS-driven banner + intro, then a two-column
 * grid: address block (from CMS footer_config) on the left, real submission
 * form on the right. Trailing "info" cards (Response time / Confidentiality /
 * Regional coverage) are rendered from the CMS as normal cards_grid.
 */
export default function ContactPage() {
  const { data: page, loading, error } = useAsync(() => getPage("contact"), []);
  const { footer } = useSite();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const initialSubject = searchParams.get("subject") || "";
  const breadcrumbs = buildBreadcrumbs(pathname);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-mist-off">
        <Loader2 className="h-8 w-8 text-mist-navy animate-spin" aria-hidden />
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 bg-mist-off text-mist-ink">
        <AlertCircle className="h-8 w-8 text-mist-navy" aria-hidden />
        <div className="text-lg font-display text-mist-navy">Content unavailable</div>
      </div>
    );
  }

  // Split sections: [0] page banner, [1] rich_text intro, [2] reach-out list_items (we replace with form+address),
  // [3+] trailing content cards
  const sections = page.sections || [];
  const banner = sections[0];
  const intro = sections[1];
  const trailing = sections.slice(3);

  return (
    <>
      <SEO seo={page.seo} fallbackTitle={page.title} />
      <div data-testid="page-contact">
        {banner && <SectionRenderer sections={[banner]} banner="page" breadcrumbs={breadcrumbs} />}
        {intro && <SectionRenderer sections={[intro]} />}
        <section className="bg-white pt-4 pb-20">
          <div className="container-mist grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-3">Reach out</div>
              <div className="mt-2 gold-divider" />
              <div className="mt-8 space-y-6">
                {footer?.address && (
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-mist-navy/[0.06] grid place-items-center shrink-0">
                      <MapPin className="h-5 w-5 text-mist-navy" aria-hidden />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1">
                        Office
                      </div>
                      <div data-testid="contact-address" className="text-sm text-mist-ink/80 leading-relaxed">
                        {footer.address}
                      </div>
                    </div>
                  </div>
                )}
                {footer?.email && (
                  <div className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-mist-navy/[0.06] grid place-items-center shrink-0">
                      <Mail className="h-5 w-5 text-mist-navy" aria-hidden />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1">
                        Email
                      </div>
                      <a
                        href={`mailto:${footer.email}`}
                        data-testid="contact-email-link"
                        className="text-sm text-mist-navy hover:text-mist-gold transition-colors underline underline-offset-4"
                      >
                        {footer.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm initialSubject={initialSubject} />
            </div>
          </div>
        </section>
        {trailing.length > 0 && <SectionRenderer sections={trailing} />}
      </div>
    </>
  );
}
